/**
 * @file src/commands/template-publish.ts
 * @description Stable CLI presentation for read-only offline verification of
 * a signed template publisher distribution snapshot.
 */
import { addPackage } from "../profile/templates/publish/add.js";
import { buildDistribution } from "../profile/templates/publish/build.js";
import {
  stageRevokePackage,
  stageRevokePublisherKey,
  stageRotatePublisherKey,
  stageRotateTapKey,
} from "../profile/templates/publish/lifecycle.js";
import { initWorkspace, type InitWorkspaceOptions, type InitWorkspaceResult } from "../profile/templates/publish/init.js";
import { resolveWorkspacePaths } from "../profile/templates/publish/workspace-paths.js";
import { verifyPublisherDistribution } from "../profile/templates/publish/verify.js";

const TERMINAL_CONTROL = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/gu;
const MAX_ERROR_MESSAGE_BYTES = 3_800;

/** Options required by `template publish verify`. */
export interface TemplatePublishVerifyOptions {
  tap: string;
  keyId: string;
  keyFile: string;
  json?: boolean;
}

/** Verify without network or writes, then print bounded public provenance only. */
export async function templatePublishVerifyCommand(
  directory: string,
  options: TemplatePublishVerifyOptions,
): Promise<number> {
  try {
    const result = await verifyPublisherDistribution(directory, options.tap, options.keyId, options.keyFile);
    if (options.json) console.log(JSON.stringify(result, null, 2));
    else printHumanResult(result);
    return 0;
  } catch (error) {
    throw new Error(boundedSafeError(error));
  }
}

function printHumanResult(result: Awaited<ReturnType<typeof verifyPublisherDistribution>>): void {
  console.log("Verified template publisher distribution.");
  console.log(`Scope: ${result.scope}`);
  console.log(`Continuity: ${result.continuity}`);
  console.log(`Tap: ${safeTerminalText(result.tap)}`);
  console.log(`Sequence: ${result.sequence}`);
  console.log(`Tap key: ${safeTerminalText(result.tapKeyId)}`);
  console.log(`Packages: ${result.packageCount}`);
}

function safeTerminalText(value: string): string {
  return value.replace(TERMINAL_CONTROL, "�");
}

function boundedSafeError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const safe = safeTerminalText(message || "publisher distribution verification failed");
  const bytes = Buffer.from(safe, "utf8");
  if (bytes.length <= MAX_ERROR_MESSAGE_BYTES) return safe;
  return `${bytes.subarray(0, MAX_ERROR_MESSAGE_BYTES - 3).toString("utf8").replace(/�$/u, "")}…`;
}

/** Options required by `template publish init`. */
export interface TemplatePublishInitOptions {
  tap: string;
  publisher: string;
  tapKeyId?: string;
  publisherKeyId?: string;
  json?: boolean;
}

/** Create a publisher workspace with fresh tap and publisher keypairs. */
export async function templatePublishInitCommand(
  directory: string,
  options: TemplatePublishInitOptions,
): Promise<number> {
  try {
    const result = await initWorkspace(directory, initOptions(options));
    if (options.json) console.log(JSON.stringify(publicInitResult(result), null, 2));
    else printInitResult(result);
    return 0;
  } catch (error) {
    throw new Error(boundedSafeError(error));
  }
}

/** Forward only the key-id overrides the operator actually supplied. */
function initOptions(options: TemplatePublishInitOptions): InitWorkspaceOptions {
  return {
    tap: options.tap,
    publisher: options.publisher,
    ...(options.tapKeyId === undefined ? {} : { tapKeyId: options.tapKeyId }),
    ...(options.publisherKeyId === undefined ? {} : { publisherKeyId: options.publisherKeyId }),
  };
}

/** The public shape: key ids and fingerprints only, never private bytes. */
function publicInitResult(result: InitWorkspaceResult): object {
  return {
    schemaVersion: 1,
    tap: result.tap,
    publisher: result.publisher,
    tapKeyId: result.tapKey.keyId,
    publisherKeyId: result.publisherKey.keyId,
    fingerprints: result.fingerprints,
  };
}

function printInitResult(result: InitWorkspaceResult): void {
  console.log("Initialized publisher workspace.");
  console.log(`Tap: ${safeTerminalText(result.tap)}`);
  console.log(`Publisher: ${safeTerminalText(result.publisher)}`);
  console.log(`Tap key: ${safeTerminalText(result.tapKey.keyId)} (${result.fingerprints.tap})`);
  console.log(`Publisher key: ${safeTerminalText(result.publisherKey.keyId)} (${result.fingerprints.publisher})`);
  console.log("Private keys are stored 0600 under keys/ and are never printed.");
  console.log("Distribute the tap public key through a channel independent of the tap.");
}

/** Options accepted by `template publish add`. */
export interface TemplatePublishAddOptions { workspace: string; packageVersion: string; json?: boolean }

/** Validate, sign, and record one template package into the workspace. */
export async function templatePublishAddCommand(
  packageFile: string,
  options: TemplatePublishAddOptions,
): Promise<number> {
  try {
    const result = await addPackage(resolveWorkspacePaths(options.workspace), packageFile, options.packageVersion);
    if (options.json) console.log(JSON.stringify({ schemaVersion: 1, ...result }, null, 2));
    else {
      console.log(result.alreadyPresent ? "Package already recorded." : "Recorded signed package.");
      console.log(`Coordinate: ${safeTerminalText(result.coordinate)}`);
      console.log(`Digest: ${result.payloadDigest}`);
    }
    return 0;
  } catch (error) {
    throw new Error(boundedSafeError(error));
  }
}

/** Options accepted by `template publish build`. */
export interface TemplatePublishBuildOptions {
  workspace: string;
  out: string;
  expiresIn: string;
  refresh?: boolean;
  force?: boolean;
  json?: boolean;
}

/** Build, verify, and publish one static distribution. */
export async function templatePublishBuildCommand(options: TemplatePublishBuildOptions): Promise<number> {
  try {
    const result = await buildDistribution(resolveWorkspacePaths(options.workspace), {
      out: options.out,
      expiresIn: options.expiresIn,
      refresh: options.refresh === true,
      force: options.force === true,
    });
    if (options.json) console.log(JSON.stringify({ schemaVersion: 1, ...result }, null, 2));
    else {
      console.log("Built and verified distribution.");
      console.log(`Sequence: ${result.sequence}`);
      console.log(`Packages: ${result.packageCount}`);
      console.log(`Index digest: ${result.indexDigest}`);
      console.log(`Output: ${safeTerminalText(result.out)}`);
    }
    return 0;
  } catch (error) {
    throw new Error(boundedSafeError(error));
  }
}

/** Options accepted by `template publish rotate`. */
export interface TemplatePublishRotateOptions { workspace: string; tapKeyId?: string; publisherKeyId?: string }

/** Stage a key rotation; it is signed by the next build at that build's sequence. */
export async function templatePublishRotateCommand(options: TemplatePublishRotateOptions): Promise<number> {
  try {
    const paths = resolveWorkspacePaths(options.workspace);
    if ((options.tapKeyId === undefined) === (options.publisherKeyId === undefined)) {
      throw new Error("rotate requires exactly one of --tap-key-id or --publisher-key-id");
    }
    if (options.publisherKeyId !== undefined) {
      await stageRotatePublisherKey(paths, options.publisherKeyId);
      console.log("Staged publisher key rotation. It is signed by the next build, at that build's sequence.");
      console.log("Every package will be re-signed with the successor key; digests do not change.");
      return 0;
    }
    await stageRotateTapKey(paths, options.tapKeyId as string);
    console.log("Staged tap ROOT key rotation. It is signed by the next build.");
    console.log("");
    console.log("WARNING: a tap-root rotation is carried by exactly ONE index. A client that does");
    console.log("not refresh while that index is the published one cannot verify the new root and");
    console.log("must forget and re-add the tap, re-pinning your key out of band. Keep that index");
    console.log("published long enough for your users to refresh, and announce the new fingerprint.");
    return 0;
  } catch (error) {
    throw new Error(boundedSafeError(error));
  }
}

/** Options accepted by `template publish revoke`. */
export interface TemplatePublishRevokeOptions {
  workspace: string;
  reason: string;
  packageDigest?: string;
  publisherKeyId?: string;
}

/** Stage a revocation; it is published by the next build and accumulates forever. */
export async function templatePublishRevokeCommand(options: TemplatePublishRevokeOptions): Promise<number> {
  try {
    const paths = resolveWorkspacePaths(options.workspace);
    if ((options.packageDigest === undefined) === (options.publisherKeyId === undefined)) {
      throw new Error("revoke requires exactly one of --package-digest or --publisher-key-id");
    }
    if (options.packageDigest !== undefined) await stageRevokePackage(paths, options.packageDigest, options.reason);
    else await stageRevokePublisherKey(paths, options.publisherKeyId as string, options.reason);
    console.log("Staged revocation. It is published by the next build and accumulates permanently.");
    return 0;
  } catch (error) {
    throw new Error(boundedSafeError(error));
  }
}
